import { DocumentReference, deleteField, setDoc } from 'firebase/firestore'
import { ref, deleteObject } from 'firebase/storage'
import { useState } from 'react'
import FirestoreUser, {
  DiagnosisDocument,
  DiagnosisDocumentDeletion,
  isDocumentDeleted,
} from '../../../../types/FirestoreUser'
import { primaryServices } from '../../../../utils/firebaseServiceContext'

interface UseDeleteDiagnosisDocumentParams {
  userRef: DocumentReference<FirestoreUser>
  diagnosisDocumentation: DiagnosisDocument[] | undefined
  existingDeletions: DiagnosisDocumentDeletion[] | undefined
  adminName: string
  onSuccess?: () => void
  onError?: (error: unknown) => void
}

/**
 * Extract storage path from Firebase Storage download URL
 * URLs are in format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token=...
 */
function getStoragePathFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname

    // Extract the path after '/o/'
    const match = pathname.match(/\/o\/(.+)$/)
    if (match) {
      // Decode the URL-encoded path
      return decodeURIComponent(match[1])
    }
    return null
  } catch {
    return null
  }
}

/**
 * Count active (non-deleted) documents in the array
 */
function countActiveDocuments(docs: DiagnosisDocument[]): number {
  return docs.filter((doc) => !isDocumentDeleted(doc)).length
}

export function useDeleteDiagnosisDocument({
  userRef,
  diagnosisDocumentation,
  existingDeletions,
  adminName,
  onSuccess,
  onError,
}: UseDeleteDiagnosisDocumentParams) {
  const [isDeleting, setIsDeleting] = useState(false)

  async function deleteDocument(documentIndex: number): Promise<void> {
    if (!diagnosisDocumentation || documentIndex < 0) {
      return
    }

    const doc = diagnosisDocumentation[documentIndex]
    if (!doc) {
      return
    }

    setIsDeleting(true)

    try {
      // Get the document URL
      const url = typeof doc === 'string' ? doc : doc.url
      const uploadedAt =
        typeof doc === 'object' && 'uploadedAt' in doc
          ? doc.uploadedAt
          : new Date().toISOString()

      // Prepare the updated document with soft delete fields
      const updatedDoc = {
        url,
        uploadedAt,
        deleted: true,
        deletedAt: new Date().toISOString(),
        deletedBy: adminName,
      }

      // Create a new array with the updated document
      const updatedDocs = [...diagnosisDocumentation]
      updatedDocs[documentIndex] = updatedDoc

      // Create deletion history entry (document number is 1-indexed)
      const deletionEntry: DiagnosisDocumentDeletion = {
        documentNumber: documentIndex + 1,
        deletedBy: adminName,
        deletedAt: new Date().toISOString(),
      }

      // Prepare the update object
      const updateData: Record<string, unknown> = {
        profile: {
          diagnosisDocumentation: updatedDocs,
        },
        diagnosisDocumentDeletions: [
          ...(existingDeletions || []),
          deletionEntry,
        ],
      }

      // Check if this was the last active document
      const activeCountBefore = countActiveDocuments(diagnosisDocumentation)
      if (activeCountBefore === 1) {
        // This is the last document being deleted, reset verification status
        updateData.profile = {
          diagnosisDocumentation: updatedDocs,
          diagnosisVerified: false,
          diagnosisVerificationDate: deleteField(),
          diagnosisVerifiedBy: deleteField(),
          diagnosisDateVerified: false,
          diagnosisDateVerificationDate: deleteField(),
          diagnosisDateVerifiedBy: deleteField(),
        }
      }

      // Update Firestore
      await setDoc(userRef, updateData as Partial<FirestoreUser>, {
        merge: true,
      })

      // Attempt to delete from Storage (silently handle errors)
      const storagePath = getStoragePathFromUrl(url)
      if (storagePath) {
        try {
          const storage = primaryServices.storage
          const storageReference = ref(storage, storagePath)
          await deleteObject(storageReference)
        } catch {
          // Silently ignore storage deletion errors (file may already be deleted)
          console.log(
            'Storage file not found or already deleted, continuing...'
          )
        }
      }

      onSuccess?.()
    } catch (error) {
      onError?.(error)
    } finally {
      setIsDeleting(false)
    }
  }

  return {
    deleteDocument,
    isDeleting,
  }
}
